// src/pages/feedback/components/CommentList.tsx
//
// 댓글 목록. 수정은 다이얼로그가 아니라 **인라인 토글**이다 — 두 줄짜리
// 텍스트를 고치려고 모달을 띄우는 건 과하다.
//
// 본인 댓글의 [수정]/[삭제]는 hover 시에만 노출하지 않고 항상 표시한다
// (모바일에 hover 가 없다). 본인 판정은 UI 힌트일 뿐이고 최종 방어선은
// 서버 403 이다.
//
// 본문은 plain text 로만 렌더한다 — dangerouslySetInnerHTML 을 어떤 경로에서도
// 쓰지 않으며, 줄바꿈은 CSS(white-space: pre-wrap)로만 처리한다.

import { useState } from "react";
import { Box, Button, CircularProgress, TextField, Typography } from "@mui/material";

import { designTokens } from "../../../theme.ts";
import { fmtDateTime } from "../../../utils/dateFormat.ts";
import {
  authorLabel,
  FeedbackCommentDto,
} from "../../../contracts/feedback/types.ts";
import { FEEDBACK_LIMITS, validateCommentBody } from "../../../contracts/feedback/schemas.ts";

const T = designTokens;

const C = {
  ink: "#171A1E",
  sub: "#6B7178",
  caption: "#9AA0A6",
  rowLine: "#EDEFF1",
  field: "#F9FAFB",
};

interface CommentListProps {
  comments: FeedbackCommentDto[];
  /** useAuth0().user?.sub — 본인 판정용 */
  currentSub?: string;
  onUpdate: (commentId: string, body: string) => Promise<void>;
  /** 부모가 ConfirmDialog 를 연다. */
  onDelete: (commentId: string) => void;
  /** 요청 진행 중인 댓글 id */
  busyId?: string | null;
}

/** 수정 시각이 작성 시각보다 유의미하게 뒤면 "수정됨"을 붙인다. */
const wasEdited = (c: FeedbackCommentDto): boolean => {
  const created = Date.parse(c.createdTimestamp);
  const modified = Date.parse(c.modifiedTimestamp);
  if (Number.isNaN(created) || Number.isNaN(modified)) return false;
  return modified - created > 1000;
};

export default function CommentList({
  comments,
  currentSub,
  onUpdate,
  onDelete,
  busyId = null,
}: CommentListProps) {
  const [editingId, setEditingId] = useState<string | null>(null);
  const [draft, setDraft] = useState("");
  const [draftError, setDraftError] = useState<string | null>(null);

  if (comments.length === 0) {
    return (
      <Typography sx={{ fontFamily: T.fonts.sans, fontSize: 13.5, color: C.caption, py: 1 }}>
        아직 댓글이 없습니다. 첫 댓글을 남겨 보세요.
      </Typography>
    );
  }

  const startEdit = (c: FeedbackCommentDto) => {
    setEditingId(c.id);
    setDraft(c.body);
    setDraftError(null);
  };

  const cancelEdit = () => {
    setEditingId(null);
    setDraft("");
    setDraftError(null);
  };

  const saveEdit = async (commentId: string) => {
    const message = validateCommentBody(draft);
    if (message) {
      setDraftError(message);
      return;
    }
    try {
      await onUpdate(commentId, draft.trim());
      setEditingId(null);
      setDraft("");
      setDraftError(null);
    } catch {
      // 부모가 스낵바로 알린다. 편집 상태를 유지해 입력이 사라지지 않게 한다.
    }
  };

  return (
    <Box>
      {comments.map((c, i) => {
        const isMine = Boolean(currentSub) && c.authorSub === currentSub;
        const isEditing = editingId === c.id;
        const busy = busyId === c.id;
        const name = authorLabel(c);

        return (
          <Box
            key={c.id}
            sx={{
              display: "flex",
              gap: 1.25,
              py: 1.75,
              borderBottom: i === comments.length - 1 ? "none" : `1px solid ${C.rowLine}`,
            }}
          >
            {/* 아바타 — DashboardLayout 관례(잉크 바탕 + 흰 이니셜) */}
            <Box
              aria-hidden
              sx={{
                width: 26,
                height: 26,
                flexShrink: 0,
                borderRadius: "50%",
                bgcolor: C.ink,
                color: "#FFFFFF",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontFamily: T.fonts.sans,
                fontSize: 12,
                fontWeight: 700,
                lineHeight: 1,
              }}
            >
              {name.trim().charAt(0).toUpperCase() || "U"}
            </Box>

            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  flexWrap: "wrap",
                  gap: 0.75,
                  mb: 0.5,
                }}
              >
                <Typography
                  sx={{ fontFamily: T.fonts.sans, fontSize: 13.5, fontWeight: 600, color: C.ink }}
                >
                  {name}
                </Typography>
                <Typography
                  sx={{
                    fontFamily: T.fonts.sans,
                    fontSize: 12.5,
                    color: C.caption,
                    fontVariantNumeric: "tabular-nums",
                  }}
                >
                  {fmtDateTime(c.createdTimestamp)}
                  {wasEdited(c) ? " · 수정됨" : ""}
                </Typography>

                <Box sx={{ flex: 1 }} />

                {isMine && !isEditing && (
                  <Box sx={{ display: "flex", gap: 0.25, flexShrink: 0 }}>
                    <Button size="small" onClick={() => startEdit(c)} disabled={busy}>
                      수정
                    </Button>
                    <Button
                      size="small"
                      color="error"
                      onClick={() => onDelete(c.id)}
                      disabled={busy}
                    >
                      삭제
                    </Button>
                  </Box>
                )}
              </Box>

              {isEditing ? (
                <Box>
                  <TextField
                    value={draft}
                    onChange={(e) => {
                      setDraft(e.target.value);
                      if (draftError) setDraftError(null);
                    }}
                    multiline
                    minRows={3}
                    fullWidth
                    size="small"
                    autoFocus
                    error={Boolean(draftError)}
                    helperText={
                      draftError ??
                      `${draft.length.toLocaleString("ko-KR")} / ${FEEDBACK_LIMITS.comment.toLocaleString("ko-KR")}`
                    }
                    sx={{
                      "& .MuiOutlinedInput-root": { bgcolor: C.field, borderRadius: "8px" },
                      "& textarea": {
                        fontFamily: T.fonts.sans,
                        fontSize: 14,
                        lineHeight: 1.7,
                      },
                      "& .MuiFormHelperText-root": { textAlign: draftError ? "left" : "right", mx: 0 },
                    }}
                  />
                  <Box sx={{ display: "flex", gap: 1, justifyContent: "flex-end", mt: 1 }}>
                    <Button size="small" variant="outlined" onClick={cancelEdit} disabled={busy}>
                      취소
                    </Button>
                    <Button
                      size="small"
                      variant="contained"
                      onClick={() => saveEdit(c.id)}
                      disabled={busy}
                      startIcon={
                        busy ? <CircularProgress size={16} color="inherit" /> : undefined
                      }
                    >
                      저장
                    </Button>
                  </Box>
                </Box>
              ) : (
                <Typography
                  component="div"
                  sx={{
                    fontFamily: T.fonts.sans,
                    fontSize: 14,
                    lineHeight: 1.7,
                    color: C.ink,
                    whiteSpace: "pre-wrap",
                    overflowWrap: "anywhere",
                    wordBreak: "break-word",
                    maxWidth: "100%",
                  }}
                >
                  {c.body}
                </Typography>
              )}
            </Box>
          </Box>
        );
      })}
    </Box>
  );
}
